import { useEffect, useState } from 'react';
import api from '../api/client';
import { useFincaId } from '../context/FincaContext';
import PiscinaBuscador from '../components/PiscinaBuscador';
import { format } from 'date-fns';

// 1 libra = 454 gramos
const GRAMOS_POR_LIBRA = 454;

function calcularSobrevivencia(libras, peso, animalesSembrados) {
  if (!libras || !peso || !animalesSembrados) return null;
  const totalCamarones = (Number(libras) * GRAMOS_POR_LIBRA) / Number(peso);
  return (totalCamarones / Number(animalesSembrados)) * 100;
}

function formatPeso(v) {
  if (v == null) return '-';
  return Number(v).toLocaleString('es-EC', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatLibras(v) {
  if (v == null) return '-';
  return Number(v).toLocaleString('es-EC', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function formatPorcentaje(v) {
  if (v == null || isNaN(v)) return '-';
  return `${Math.round(v)}%`;
}

export default function ReporteCosecha() {
  const fincaIdContext = useFincaId();
  const [sectores, setSectores] = useState([]);
  const [piscinas, setPiscinas] = useState([]);
  const [corridas, setCorridas] = useState([]);

  const [sectorId, setSectorId] = useState('');
  const [piscinaId, setPiscinaId] = useState('');
  const [siembraId, setSiembraId] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('todas'); // activas | inactivas | todas

  const [siembraInfo, setSiembraInfo] = useState(null);
  const [piscinaInfo, setPiscinaInfo] = useState(null);
  const [cosechas, setCosechas] = useState([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    cargarSectores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  useEffect(() => {
    if (sectorId) {
      cargarPiscinas(sectorId, estadoFiltro);
    } else {
      setPiscinas([]);
    }
    setPiscinaId('');
    setSiembraId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId, estadoFiltro]);

  useEffect(() => {
    if (piscinaId) {
      cargarCorridas(piscinaId);
    } else {
      setCorridas([]);
    }
    setSiembraId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piscinaId]);

  useEffect(() => {
    if (siembraId) {
      cargarReporte();
    } else {
      setCosechas([]);
      setSiembraInfo(null);
      setPiscinaInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siembraId]);

  const cargarSectores = async () => {
    try {
      if (!fincaIdContext) {
        setSectores([]);
        return;
      }
      const { data } = await api.get(`/sectores/finca/${fincaIdContext}`);
      setSectores(data || []);
      if (data && data.length > 0) {
        if (data && data.length > 0 && !sectorId) {
        setSectorId(data[0].id);
        }
      } else {
        setSectorSel(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const cargarPiscinas = async (id, estado) => {
    try {
      const { data } = await api.get(`/piscinas/sector/${id}?incluirInactivas=true`);

      let filtradas = data;
      if (estado === 'activas') filtradas = data.filter(p => p.activo);
      else if (estado === 'inactivas') filtradas = data.filter(p => !p.activo);

      setPiscinas(filtradas);
    } catch (err) {
      console.error(err);
    }
  };

  const cargarCorridas = async (id) => {
    try {
      const { data } = await api.get(`/siembras/piscina/${id}`);
      setCorridas(data);
    } catch (err) {
      console.error(err);
      setCorridas([]);
    }
  };

  const cargarReporte = async () => {
    setCargando(true);
    try {
      const { data: siembras } = await api.get(`/siembras/piscina/${piscinaId}`);
      const siembra = siembras.find((s) => s.id === siembraId);
      setSiembraInfo(siembra || null);

      const piscina = piscinas.find((p) => p.id === piscinaId);
      setPiscinaInfo(piscina || null);

      const { data } = await api.get('/cosechas', {
        params: { siembra_id: siembraId },
      });
      setCosechas(data);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  };

  const exportarExcel = async () => {
  if (!siembraId) return;
  try {
    const res = await api.get('/cosechas/exportar/excel', {
      params: { siembra_id: siembraId },
      responseType: 'blob',
    });

    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte_cosecha_${siembraId.slice(0, 8)}_${Date.now()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error exportando:', err);
    alert('Error al exportar. Revisa la consola.');
  }
};

  const raleos = cosechas.filter((c) => c.tipo === 'raleo');
  const cosechasArr = cosechas.filter((c) => c.tipo === 'cosecha');

  const animalesSembrados = siembraInfo?.cantidad_sembrada || 0;
  const hectareas = piscinaInfo?.area_ha;

  const totalRaleoLibras = raleos.reduce((a, c) => a + Number(c.libras || 0), 0);
  const totalRaleoCamarones = raleos.reduce((a, c) => {
    if (!c.libras || !c.peso_kg) return a;
    return a + (Number(c.libras) * GRAMOS_POR_LIBRA) / Number(c.peso_kg);
  }, 0);
  const totalRaleoSobrevivencia = animalesSembrados > 0
    ? (totalRaleoCamarones / animalesSembrados) * 100
    : null;

  const totalCosechaLibras = cosechasArr.reduce((a, c) => a + Number(c.libras || 0), 0);
  const totalCosechaCamarones = cosechasArr.reduce((a, c) => {
    if (!c.libras || !c.peso_kg) return a;
    return a + (Number(c.libras) * GRAMOS_POR_LIBRA) / Number(c.peso_kg);
  }, 0);
  const totalCosechaSobrevivencia = animalesSembrados > 0
    ? (totalCosechaCamarones / animalesSembrados) * 100
    : null;

  return (
    <div className="p-1">
      {/* Filtros + botón Excel */}
      <div className="bg-white p-4 rounded shadow mb-6 flex flex-wrap gap-4 items-end">
        <label className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 mb-1">Sector</span>
          <select
            value={sectorId}
            onChange={(e) => setSectorId(e.target.value)}
            className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm min-w-[150px]"
          >
            <option value="">-- Selecciona --</option>
            {sectores.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 mb-1">Estado piscina</span>
          <select
            value={estadoFiltro}
            onChange={(e) => setEstadoFiltro(e.target.value)}
            className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm min-w-[130px]"
            disabled={!sectorId}
          >
            <option value="todas">Todas</option>
            <option value="activas">Activas</option>
            <option value="inactivas">Inactivas (cosechadas)</option>
          </select>
        </label>

        <div className="flex flex-col min-w-[180px]">
          <PiscinaBuscador
            piscinas={piscinas}
            value={piscinaId}
            onChange={(id) => setPiscinaId(id)}
            disabled={!sectorId}
          />
        </div>

        <label className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 mb-1">Corrida</span>
          <select
            value={siembraId}
            onChange={(e) => setSiembraId(e.target.value)}
            className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm min-w-[180px]"
            disabled={!piscinaId || corridas.length === 0}
          >
            <option value="">
              {corridas.length === 0 && piscinaId
                ? 'Sin corridas'
                : '-- Selecciona --'}
            </option>
            {corridas.map((c) => (
              <option key={c.id} value={c.id}>
                #{c.numero_corrida || '?'} - {format(new Date(c.fecha_siembra), 'dd/MM/yyyy')}
              </option>
            ))}
          </select>
        </label>

        {/* Botón Excel — solo ícono, alineado a la derecha */}
        <button
          type="button"
          onClick={exportarExcel}
          disabled={!siembraId || cosechas.length === 0}
          title="Exportar a Excel"
          className="ml-auto self-end px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
          style={{ height: '42px' }}
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" fill="white" />
            <path
              d="M8 8l8 8M16 8l-8 8"
              stroke="#16A34A"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {!siembraId && (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          Selecciona un sector, una piscina y una corrida para ver el reporte.
        </div>
      )}

      {cargando && (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          Cargando...
        </div>
      )}

      {!cargando && siembraId && siembraInfo && (
        <>
          {/* Info general */}
          <div className="bg-blue-50 p-4 rounded shadow mb-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <div className="text-xs text-gray-600">Piscina</div>
              <div className="font-bold text-lg">{piscinaInfo?.codigo || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-600">Estado</div>
              <div className="font-bold text-lg">
                {piscinaInfo?.activo
                  ? <span className="text-green-700">Activa</span>
                  : <span className="text-red-700">Inactiva</span>}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600">Hectáreas</div>
              <div className="font-bold text-lg">
                {hectareas != null ? Number(hectareas).toFixed(2) : '-'}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600">Animales sembrados</div>
              <div className="font-bold text-lg">
                {Number(animalesSembrados).toLocaleString('es-EC')}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600">Fecha siembra</div>
              <div className="font-bold text-lg">
                {siembraInfo.fecha_siembra
                  ? format(new Date(siembraInfo.fecha_siembra), 'dd/MM/yyyy')
                  : '-'}
              </div>
            </div>
          </div>

          {/* Tabla Raleos */}
          <div className="bg-white rounded shadow overflow-hidden mb-6">
            <div className="px-4 py-3 bg-orange-50 border-b border-orange-200">
              <h2 className="font-bold text-orange-800 text-lg">— Raleos —</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                    <th className="text-right px-4 py-3 font-semibold">Peso (g)</th>
                    <th className="text-right px-4 py-3 font-semibold">Libras</th>
                    <th className="text-right px-4 py-3 font-semibold">Sobrevivencia</th>
                  </tr>
                </thead>
                <tbody>
                  {raleos.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                        No hay raleos registrados para esta corrida
                      </td>
                    </tr>
                  ) : (
                    raleos.map((r) => {
                      const sob = calcularSobrevivencia(r.libras, r.peso_kg, animalesSembrados);
                      return (
                        <tr key={r.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-2">
                            {format(new Date(r.fecha), 'dd/MM/yyyy')}
                          </td>
                          <td className="px-4 py-2 text-right">{formatPeso(r.peso_kg)}</td>
                          <td className="px-4 py-2 text-right">{formatLibras(r.libras)}</td>
                          <td className="px-4 py-2 text-right font-semibold text-orange-700">
                            {formatPorcentaje(sob)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                  {raleos.length > 0 && (
                    <tr className="border-t-2 border-orange-300 bg-orange-50 font-bold">
                      <td className="px-4 py-2 text-orange-800">TOTAL RALEO</td>
                      <td className="px-4 py-2 text-right text-orange-800">-</td>
                      <td className="px-4 py-2 text-right text-orange-800">
                        {formatLibras(totalRaleoLibras)}
                      </td>
                      <td className="px-4 py-2 text-right text-orange-800">
                        {formatPorcentaje(totalRaleoSobrevivencia)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabla Cosechas */}
          <div className="bg-white rounded shadow overflow-hidden">
            <div className="px-4 py-3 bg-green-50 border-b border-green-200">
              <h2 className="font-bold text-green-800 text-lg">— Cosechas —</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                    <th className="text-right px-4 py-3 font-semibold">Peso (g)</th>
                    <th className="text-right px-4 py-3 font-semibold">Libras</th>
                    <th className="text-right px-4 py-3 font-semibold">Sobrevivencia</th>
                  </tr>
                </thead>
                <tbody>
                  {cosechasArr.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                        No hay cosechas registradas para esta corrida
                      </td>
                    </tr>
                  ) : (
                    cosechasArr.map((c) => {
                      const sob = calcularSobrevivencia(c.libras, c.peso_kg, animalesSembrados);
                      return (
                        <tr key={c.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-2">
                            {format(new Date(c.fecha), 'dd/MM/yyyy')}
                          </td>
                          <td className="px-4 py-2 text-right">{formatPeso(c.peso_kg)}</td>
                          <td className="px-4 py-2 text-right">{formatLibras(c.libras)}</td>
                          <td className="px-4 py-2 text-right font-semibold text-green-700">
                            {formatPorcentaje(sob)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                  {cosechasArr.length > 0 && (
                    <tr className="border-t-2 border-green-300 bg-green-50 font-bold">
                      <td className="px-4 py-2 text-green-800">TOTAL COSECHAS</td>
                      <td className="px-4 py-2 text-right text-green-800">-</td>
                      <td className="px-4 py-2 text-right text-green-800">
                        {formatLibras(totalCosechaLibras)}
                      </td>
                      <td className="px-4 py-2 text-right text-green-800">
                        {formatPorcentaje(totalCosechaSobrevivencia)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}